import type { AuctisyncSupabase } from './client.js';
import { DataRequestError } from './request-error.js';

/**
 * What a CATALOGUER sees — the field-intake queries, shared by the website's
 * `/admin/catalog` page and the native Cataloging app.
 *
 * 🔴 THESE ARE RULES, NOT CONVENIENCE WRAPPERS, which is why they live here.
 * "Which auctions may be catalogued" and "which lots belong to one" decide what
 * a warehouse worker is allowed to photograph. The website reached those
 * answers with inline queries because it was the only client; the moment the
 * app became the second, a copy would have been two opinions about a
 * permission-shaped question, disagreeing the first time either changed.
 * `feedback_no_mechanical_divergence`.
 *
 * ⚠️ NONE OF THESE FILTER BY TENANT and that is deliberate. RLS scopes every
 * one of them to the staff member's own house. A client-side `eq('tenant_id')`
 * here would look like the security boundary while being decoration — and if
 * the policies were ever wrong, it would HIDE the fact rather than surface it.
 */

/**
 * The statuses a cataloguer may work on.
 *
 * 🔴 A CLOSED SALE IS NOT ON THIS LIST. Cataloguing writes photos and stamps
 * `cataloged_at` against a lot; doing that to a sale that has already run edits
 * the record of something people bid real money on. Intake happens BEFORE a
 * sale opens, so these are the four states that precede it.
 */
export const CATALOGABLE_AUCTION_STATUSES = ['draft', 'queued', 'preview', 'active'] as const;

/**
 * ⚠️ THE DATABASE'S OWN FIELD NAMES, not camelCase, and that is the point.
 *
 * The website's `/admin/catalog` page is 754 lines built on these names. A
 * prettier shape here would have meant renaming 17 call sites in a page that
 * does real cataloguing today, purely to satisfy the shape of a package — so
 * the page would have kept its inline query instead, and the rule would have
 * had two implementations after all. Matching the row makes adoption a
 * one-line change, which is what actually gets the second caller onto the
 * shared rule.
 */
export interface CatalogingAuction {
  id: string;
  title: string;
  slug: string;
  /**
   * The house's filename prefix. The upload Edge Function uses it to build
   * `{prefix}-{lot:04d}-{seq:02d}`, so a photo's name is meaningful to the
   * office team without opening it. Null until the office sets one.
   */
  photo_file_prefix: string | null;
  tenant_id: string;
  status: string;
}

export interface CatalogingLot {
  id: string;
  lot_number: number | null;
  control_number: string | null;
  title: string | null;
  /**
   * When this lot was last photographed; the office team's "needs research"
   * queue is built from it. Null means nobody has been to this lot yet — which
   * is what the app draws its progress from.
   */
  cataloged_at: string | null;
  /**
   * When the worker FINISHED the lot and moved on — what queues the AI draft.
   * 🔴 Not `cataloged_at`, which is the FIRST photo and drives the office's
   * needs-research queue. Progress stays on `cataloged_at`, so the worker and
   * the office count the same thing.
   */
  capture_completed_at: string | null;
}

/**
 * The auctions this staff member may catalogue, newest first.
 *
 * Capped at 50: a house with more open sales than that has an organisational
 * problem a picker cannot solve, and an uncapped list on a warehouse tablet is
 * a scroll nobody finishes.
 */
export async function fetchCatalogingAuctions(
  supabase: AuctisyncSupabase,
): Promise<CatalogingAuction[]> {
  const { data, error } = await supabase
    .from('auctions')
    .select('id, title, slug, photo_file_prefix, tenant_id, status')
    .in('status', [...CATALOGABLE_AUCTION_STATUSES])
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) throw new Error(error.message);
  return data ?? [];
}

/**
 * The lots of one auction, in lot-number order.
 *
 * 🔴 NOT `fetchAuctionLots`, AND THE TWO MUST NOT BE MERGED. That one is the
 * PUBLIC read: it filters `is_visible = true` and orders by `sale_order`,
 * because a bidder should see the sale as the house has arranged it. During
 * intake almost nothing is visible yet — the lots are empty shells the office
 * created minutes ago — so the public query would hide the entire run from the
 * worker sent to photograph it. This one walks `lot_number`, which is the order
 * the items are physically in.
 *
 * A future tidy-up that notices "two functions read lots of one auction" and
 * unifies them breaks field intake silently, which is why this paragraph is
 * here rather than a one-line comment.
 *
 * THIS READS; IT CREATES NOTHING. Lots are added by `addLots` — the office's
 * "Create empty lots" and, since 2026-10-05, the Cataloging app's "Add lot"
 * (Jim: a worker may add a lot from the warehouse). Swiping past the end of the
 * run still never mints one: adding is its own, confirmed step.
 */
export async function fetchCatalogingLots(
  supabase: AuctisyncSupabase,
  auctionId: string,
): Promise<CatalogingLot[]> {
  // `lots_staff`, not `lots`: since 20260923210000 the shared `authenticated`
  // role reads only the PUBLIC columns of `lots`, and `capture_completed_at`
  // is staff-only — selecting it from `lots` is 42501 for the whole query
  // ("permission denied for table lots" on Jim's phone, 2026-09-24). The
  // view is row-limited to the caller's own houses, which is exactly who a
  // cataloguer is.
  const { data, error } = await supabase
    .from('lots_staff')
    .select('id, lot_number, control_number, title, cataloged_at, capture_completed_at')
    .eq('auction_id', auctionId)
    .order('lot_number', { ascending: true });
  if (error) throw new Error(error.message);
  return data ?? [];
}

/**
 * How far through a run the worker is.
 *
 * Derived from `catalogedAt` rather than from a photo count, because that is
 * the column the OFFICE's "needs research" queue reads. A progress number built
 * from anything else would drift from the one the office sees, and the two
 * teams would disagree about whether a sale was ready.
 */
export function catalogingProgress(lots: CatalogingLot[]): {
  done: number;
  total: number;
  /** The first lot nobody has been to yet — where "resume" should land. */
  firstUntouched: CatalogingLot | null;
} {
  const done = lots.filter((l) => l.cataloged_at !== null).length;
  return {
    done,
    total: lots.length,
    firstUntouched: lots.find((l) => l.cataloged_at === null) ?? null,
  };
}

/** A lot `addLots` added — or found already added, for a retried id. */
export interface AddedLot {
  id: string;
  lot_number: number;
  control_number: string | null;
}

/**
 * Add lots to a sale: the office's "Create empty lots" and the Cataloging
 * app's "Add lot" (Jim, 2026-10-05), through ONE database rule
 * (`public.add_lots`).
 *
 * 🔴 THE CALLER MINTS EACH LOT'S ID, AND THE SERVER NUMBERS IT. The id is what
 * makes the call safe to repeat — the app queues an "Add lot" (offline, maybe)
 * and a retry of an attempt whose answer never arrived returns the same lot.
 * The number comes after the sale's highest, under a per-sale lock, so two
 * tablets — or a tablet and the office — never take the same one. Pass
 * `lotNumbers` (one per id) only to ask for particular numbers; a taken one is
 * refused.
 *
 * Every lot added is a DRAFT bidders cannot see until the office publishes it.
 * `inWarehouse` records that a worker added it from the app, so the office
 * knows to review it. Refusals keep the database's code (`DataRequestError`),
 * so the app's queue can tell a refusal from the network.
 */
export async function addLots(
  supabase: AuctisyncSupabase,
  args: {
    auctionId: string;
    lotIds: readonly string[];
    lotNumbers?: readonly number[];
    inWarehouse?: boolean;
  },
): Promise<AddedLot[]> {
  const { data, error } = await supabase.rpc('add_lots', {
    p_auction_id: args.auctionId,
    p_lot_ids: [...args.lotIds],
    ...(args.lotNumbers ? { p_lot_numbers: [...args.lotNumbers] } : {}),
    p_in_warehouse: args.inWarehouse ?? false,
  });
  if (error) throw new DataRequestError(error);
  return data ?? [];
}

/**
 * The lots a worker means when they type into "Go to lot" — by lot number, or
 * by control number for the office's labels.
 *
 *   - nothing typed: every lot, in run order;
 *   - digits: the lot with exactly that number first, then every lot whose
 *     number starts with them ("1" → 1, 10–19, 100…), in number order; then
 *     lots whose control number contains them;
 *   - anything else: lots whose control number contains it, ignoring case.
 *
 * A lot not yet numbered (added offline, still on its way) matches only an
 * empty search.
 */
export function searchCatalogingLots<
  L extends Pick<CatalogingLot, 'lot_number' | 'control_number'>,
>(lots: readonly L[], query: string): L[] {
  const q = query.trim().toLowerCase();
  if (q === '') return [...lots];
  const byControl = (l: L) => (l.control_number ?? '').toLowerCase().includes(q);
  if (!/^\d+$/.test(q)) return lots.filter(byControl);
  const wanted = Number(q);
  const byNumber = lots
    .filter((l) => l.lot_number !== null && String(l.lot_number).startsWith(String(wanted)))
    .sort((a, b) => {
      if (a.lot_number === wanted) return -1;
      if (b.lot_number === wanted) return 1;
      return (a.lot_number ?? 0) - (b.lot_number ?? 0);
    });
  const seen = new Set(byNumber);
  return [...byNumber, ...lots.filter((l) => !seen.has(l) && byControl(l))];
}
